import { BalloonEventModel, EventDateModel } from 'ts-core'
import React from 'react'
import { CategoryBadge, TagBadge, useBalloonEventsMetadata, useTheme, useTranslations, useUser } from '../..';
import { dateCountDown, formatDate } from '../../utils/dateCountDown';

/**
 * Pick the next upcoming date, falling back to the most recent past one.
 * Used for the meta strip — the detailed `TimeSection` still shows the full list.
 */
function pickUpcoming(event: BalloonEventModel): EventDateModel | null {
    if (!event.dates?.length) return null;
    const now = Date.now();
    const next = event.dates.find((d) => d.start.getTime() > now);
    return next ?? event.dates[event.dates.length - 1];
}

export function HeadSection({ event }: { event: BalloonEventModel }) {

    const { translated, language } = useTranslations();
    const { user } = useUser();
    const { isSystemTag } = useBalloonEventsMetadata();
    const theme = useTheme();
    const canViewSystemTags = user?.hasPermission('view-tags-index') ?? false;
    const visibleTags = event.fullTags.filter((tag) => canViewSystemTags || !isSystemTag(tag.slug));

    const upcoming = pickUpcoming(event);
    const upcomingText = upcoming
        ? (upcoming.realEnd ? upcoming.formattedBeginEnd : formatDate(upcoming.start))
        : null;
    const upcomingCountdown =
        upcoming && upcoming.start.getTime() > Date.now()
            ? dateCountDown(upcoming, translated)
            : null;

    const hasMeta = !!(event.locationName || upcomingText);

    return (
        <div className='balloon-section header-section mt-4 flex flex-col my-4'>
            <h2 className='text-xl my-2 font-semibold flex items-center text-balloon uppercase'>
                {event.title}
            </h2>
            {event.teaser?.getText(language).trim() && (
                <div className='text-md my-2' dangerouslySetInnerHTML={{ __html: event.teaser.getText(language) }} />
            )}
            {hasMeta && (
                <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mt-1 mb-2'>
                    {event.locationName && (
                        <span className='inline-flex items-center gap-1.5'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth='1.5'
                                stroke='currentColor'
                                className='w-4 h-4'
                                style={{ color: theme.primaryColor }}
                            >
                                <path strokeLinecap='round' strokeLinejoin='round' d='M15 10.5a3 3 0 11-6 0 3 3 0 016 0z' />
                                <path strokeLinecap='round' strokeLinejoin='round' d='M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z' />
                            </svg>
                            <span className='truncate max-w-[260px] md:max-w-none'>{event.locationName}</span>
                        </span>
                    )}
                    {upcomingText && (
                        <span className='inline-flex items-center gap-1.5'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                                strokeWidth='1.5'
                                stroke='currentColor'
                                className='w-4 h-4'
                                style={{ color: theme.primaryColor }}
                            >
                                <path strokeLinecap='round' strokeLinejoin='round' d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z' />
                            </svg>
                            {upcomingCountdown && (
                                <span className='font-medium text-gray-700'>{upcomingCountdown}</span>
                            )}
                            <span>{upcomingText}</span>
                        </span>
                    )}
                </div>
            )}
            <div className='flex flex-wrap gap-2 mt-2'>
                <div className='flex flex-wrap gap-2'>
                    {event.categories.map(c => <CategoryBadge language={language} key={c.id} category={c} href={`/categories/${c.slug}`} />)}
                </div>
                <div className='flex flex-wrap gap-2'>
                    {visibleTags.map((tag) => (
                        <TagBadge key={tag.id} tag={tag} isSystem={isSystemTag(tag.slug)} />
                    ))}
                </div>

            </div>

        </div>
    )
}
